/**
 * Core Configuration Context
 * 
 * Provides tenant configuration to core modules without importing from tenants.
 * This context is initialized in App.tsx with the active tenant's configuration.
 * 
 * Also maintains a global config object that can be accessed synchronously
 * for store initialization (RTK Query APIs need config at creation time).
 */

import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { TenantConfig } from 'src/core/types/appConfig';

interface ConfigContextValue {
  config: TenantConfig;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

/**
 * Global config object for synchronous access (e.g., in store setup)
 * This can be set directly (for store initialization) or by ConfigProvider
 */
let globalConfig: TenantConfig | null = null;

/**
 * Initialize global config (called from store setup before React renders)
 */
export function initializeGlobalConfig(config: TenantConfig) {
  globalConfig = config;
}

/**
 * Get the global config synchronously
 * Used by store setup and other non-React code
 */
export function getGlobalConfig(): TenantConfig {
  if (!globalConfig) {
    throw new Error('Global config not initialized. Make sure store.ts calls initializeGlobalConfig() or ConfigProvider is set up in App.tsx.');
  }
  return globalConfig;
}

interface ConfigProviderProps {
  config: TenantConfig;
  children: ReactNode;
}

/**
 * ConfigProvider - Provides tenant configuration to core modules
 * Must be initialized in App.tsx with the active tenant's config
 * 
 * Note: The global config is already initialized in store.ts, but this
 * ensures it stays in sync and provides React context for hooks.
 */
export function ConfigProvider({ config, children }: ConfigProviderProps) {
  // Update global config when provider mounts/updates
  useEffect(() => {
    globalConfig = config;
  }, [config]);

  return (
    <ConfigContext.Provider value={{ config }}>
      {children}
    </ConfigContext.Provider>
  );
}

/**
 * useConfig - Hook to access tenant configuration from core modules
 * @throws Error if used outside ConfigProvider
 */
export function useConfig(): TenantConfig {
  const context = useContext(ConfigContext);
  
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider. Make sure App.tsx wraps the app with ConfigProvider.');
  }
  
  return context.config;
}

/**
 * useApiUrl - Convenience hook to get the API URL
 */
export function useApiUrl(): string {
  const config = useConfig();
  return config.api.url;
}

